import { Button, Divider, Grid, Group, Modal, ScrollArea } from '@mantine/core';

import { useCallback, useState } from 'react';

import { useParams } from 'react-router-dom';

import { useDisclosure } from '@mantine/hooks';

import SearchBar from '../SearchBar';
import { CenteredLoader, Pagination } from '..';
import ImageListCard from './ImageListCard';

import UploadImages from '../Modals/UploadImages';

import { defaultModalProps } from '../../utils/mantineUtils';
import useImageListData, {
  DEFAULT_IMAGE_SORTING_FIELD,
  IMAGE_LABEL_TO_DB_FIELD_MAP, IMAGE_LABEL_TO_DIRECTION
} from '../../hooks/useImageListData';
import TableEmptyState, { type TableEmptyStateProps } from '../Table/TableEmptyState';
import { FileUploadIcon } from '../../icons';
import SortingSelector from '../SortingSelector/SortingSelector';
import useOrgPermissions from '../../hooks/useOrgPermissions';

interface Props {
  title: React.ReactNode
}
const EMPTY_IMAGES_SEARCH_STATE_TEXT = 'No matching images available.';
const EMPTY_STATE_IMAGES_TEXT = 'No images available.';
const ImagesList: React.FC<Props> = (props: Props) => {
  const { title } = props;
  const { organization } = useParams();
  const { userHasPermission } = useOrgPermissions();
  const userCanUploadImage = userHasPermission('upload_image');
  const [opened, handlers] = useDisclosure();
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const {
    images,
    isLoading,
    setPageIndex,
    pagination,
    pageCount,
    sorting,
    onSortingChange
  } = useImageListData(
    {
      organizationSlug: organization ?? '',
      thumbnailSize: 512,
      searchTerm
    }
  );
  const handleUpdateSearchTerm = useCallback(() => {
    setSearchTerm(currentSearchTerm);
    onSortingChange([{ id: '', desc: true }]);
  }, [currentSearchTerm]);

  const handleClearSearchTerm = useCallback(() => {
    setCurrentSearchTerm('');
    setSearchTerm('');
    onSortingChange([{ id: DEFAULT_IMAGE_SORTING_FIELD, desc: true }]);
  }, []);
  const importButton = <Button
    variant='outline'
    onClick={handlers.open}
    leftSection={<FileUploadIcon />}
  >
    Import Images
  </Button>;

  const handleSortingChange = (value: string) => {
    onSortingChange([{ id: value, desc: IMAGE_LABEL_TO_DIRECTION.get(value) === 'DESC' }]);
  };

  const emptyStateProps: TableEmptyStateProps = (searchTerm !== '')
    ? {
        text: EMPTY_IMAGES_SEARCH_STATE_TEXT
      }
    : {
        text: EMPTY_STATE_IMAGES_TEXT,
        actionButton: userCanUploadImage && importButton
      };

  return <>
    <Modal opened={opened} {...defaultModalProps}>
      <UploadImages onClose={handlers.close} />
    </Modal>
    <Group mx={16} justify='space-between'>
      {title}
      {userCanUploadImage && importButton}
    </Group>
    <Group m={16} justify='space-between'>
      <SearchBar
        disabled={isLoading}
        value={currentSearchTerm}
        onChange={setCurrentSearchTerm}
        onClear={handleClearSearchTerm}
        onSubmit={handleUpdateSearchTerm}
      />
      <SortingSelector
        currentSorting={sorting[0].id}
        onSortingChange={handleSortingChange}
        disabled={sorting[0].id === ''}
        labelToDbFieldMap={IMAGE_LABEL_TO_DB_FIELD_MAP}
      />
    </Group>
    <Divider />
    {isLoading
      ? <CenteredLoader h='100%' />
      : <ScrollArea flex={1} >
        {images.length > 0
          ? <Grid m={16} gutter={8}>
            {images?.map((image) =>
              <Grid.Col span={{ lg: 2, md: 3, sm: 4, xs: 4 }} key={image.uid} >
                <ImageListCard image={image} />
              </Grid.Col>)}
          </Grid>
          : <TableEmptyState {...emptyStateProps} />}
      </ScrollArea>}
    <Pagination
      total={pageCount}
      onChange={(value) => { setPageIndex(value - 1); }}
      value={pagination.pageIndex + 1}
    />
  </>;
};

export default ImagesList;
